import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gauge,
  FileText,
  Layers,
  ArrowRight,
  Loader2,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPTIONS = [
  {
    key: "steno",
    title: "Stenography speed & certification",
    sub: "Court reporting, CART, captioning, RPR/RMR/CRR practice.",
    Icon: Gauge,
    color: "#1E3A8A",
    bg: "#EFF6FF",
    outbound: "https://stenopacemaster.com",
    outboundLabel: "stenopacemaster.com",
    pitch:
      "We'll send you a free 7-day speed-building plan and a discount on StenoPaceMaster.",
  },
  {
    key: "draftmaster",
    title: "AI legal drafting & contracts",
    sub: "Contracts, motions, briefs, real-estate, employment docs.",
    Icon: FileText,
    color: "#065F46",
    bg: "#ECFDF5",
    outbound: "https://draftmaster.ai",
    outboundLabel: "draftmaster.ai",
    pitch:
      "We'll send you a free firm evaluation guide and a trial credit for DraftMaster.ai.",
  },
  {
    key: "both",
    title: "Tell me about both",
    sub: "I'm exploring — show me both platforms.",
    Icon: Layers,
    color: "#111111",
    bg: "#F5F5F5",
    outbound: null,
    outboundLabel: null,
    pitch:
      "We'll send you the short intro guide covering both StenoPaceMaster and DraftMaster.ai.",
  },
];

export default function InterestQuiz() {
  const [choice, setChoice] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const opt = OPTIONS.find((o) => o.key === choice);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        interest: choice,
        source: "home-quiz",
      });
      setDone(true);
      toast.success("You're in — check your inbox.");
      if (window.gtag) {
        window.gtag("event", "quiz_lead_submit", { interest: choice });
      }
      if (opt?.outbound) {
        setTimeout(() => window.open(opt.outbound, "_blank"), 700);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="interest-quiz"
      data-testid="interest-quiz"
      className="py-24 lg:py-32 border-b border-black/10 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            30-second match
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none">
            What are you here for?
          </h2>
          <p className="text-gray-600 mt-6 max-w-xl leading-relaxed">
            Pick the answer that fits — we&apos;ll point you to the right
            platform and email you a free getting-started guide.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {!choice && (
            <motion.div
              key="picker"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10"
            >
              {OPTIONS.map((o, i) => {
                const Icon = o.Icon;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      setChoice(o.key);
                      if (window.gtag) {
                        window.gtag("event", "quiz_choice", {
                          interest: o.key,
                        });
                      }
                    }}
                    data-testid={`quiz-option-${o.key}`}
                    className={
                      "text-left p-8 lg:p-10 transition-colors hover:bg-[" +
                      o.bg +
                      "] " +
                      (i < OPTIONS.length - 1
                        ? "md:border-r border-b md:border-b-0 border-black/10"
                        : "")
                    }
                    style={{ background: "white" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = o.bg)}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "white")
                    }
                  >
                    <Icon
                      size={28}
                      style={{ color: o.color }}
                      strokeWidth={1.5}
                      className="mb-6"
                    />
                    <p
                      className="text-xs tracking-[0.2em] uppercase font-semibold mb-2"
                      style={{ color: o.color }}
                    >
                      Option {i + 1}
                    </p>
                    <p className="font-serif-display text-2xl mb-3 leading-tight">
                      {o.title}
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6">
                      {o.sub}
                    </p>
                    <span
                      className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold"
                      style={{ color: o.color }}
                    >
                      Choose <ArrowRight size={14} />
                    </span>
                  </button>
                );
              })}
            </motion.div>
          )}

          {choice && !done && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12 border border-black/10 p-8 lg:p-12"
              style={{ background: opt.bg }}
            >
              <div className="lg:col-span-5">
                <p
                  className="text-xs tracking-[0.2em] uppercase font-semibold mb-3"
                  style={{ color: opt.color }}
                >
                  Your answer
                </p>
                <p className="font-serif-display text-3xl leading-tight mb-4">
                  {opt.title}
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {opt.pitch}
                </p>
                <button
                  type="button"
                  onClick={() => setChoice(null)}
                  data-testid="quiz-reset"
                  className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 hover:text-black"
                >
                  ← Change answer
                </button>
              </div>

              <form
                onSubmit={submit}
                className="lg:col-span-7 bg-white p-8 border border-black/10 space-y-6"
                data-testid="quiz-form"
              >
                <div>
                  <label className="text-xs tracking-widest uppercase text-gray-500 mb-2 block">
                    Work email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@firm.com"
                    data-testid="quiz-email"
                    className="border-b border-black/20 bg-transparent rounded-none px-0 py-3 focus:border-black focus:outline-none w-full font-sans"
                  />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  One short email. No spam. Unsubscribe anytime.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quiz-submit"
                  className="btn-sharp inline-flex items-center gap-2 disabled:opacity-50 text-white"
                  style={{ background: opt.color }}
                >
                  {loading && <Loader2 size={14} className="animate-spin" />}
                  Send My Guide
                  <ArrowRight size={14} />
                </button>
              </form>
            </motion.div>
          )}

          {done && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              data-testid="quiz-success"
              className="border border-black/10 p-10 lg:p-16 bg-white grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
            >
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="h-10 w-10 flex items-center justify-center"
                    style={{ background: opt.color, color: "white" }}
                  >
                    <Check size={20} />
                  </div>
                  <p
                    className="text-xs tracking-[0.2em] uppercase font-semibold"
                    style={{ color: opt.color }}
                  >
                    You&apos;re in
                  </p>
                </div>
                <p className="font-serif-display text-4xl leading-tight mb-4">
                  Guide is on its way.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Check your inbox in the next minute. In the meantime, you can
                  jump straight to{" "}
                  {opt.outbound ? (
                    <a
                      href={opt.outbound}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: opt.color }}
                    >
                      {opt.outboundLabel}
                    </a>
                  ) : (
                    "either platform"
                  )}{" "}
                  to start.
                </p>
              </div>
              {opt.outbound && (
                <div className="lg:col-span-5">
                  <a
                    href={opt.outbound}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quiz-outbound-cta"
                    className="btn-sharp inline-flex items-center gap-2 text-white w-full justify-center"
                    style={{ background: opt.color }}
                  >
                    Open {opt.outboundLabel}
                    <ArrowRight size={14} />
                  </a>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
